import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import axios from "axios";
import { fetchSession } from "../auth";

export type ConfigSettings = {
  id: number;
  maintenance: boolean;
  training: boolean;
  dryrun: boolean;
  from_name: string;
};

export type ConfigContextValue = {
  settings: ConfigSettings;
  updateConfig: (newSettings: ConfigSettings) => Promise<unknown>;
  refreshConfig: () => Promise<void>;
  isLoading: boolean;
};

export const ConfigContext = createContext<ConfigContextValue | undefined>(
  undefined
);

type ConfigProviderProps = {
  children: ReactNode;
};

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [settings, setSettings] = useState<ConfigSettings>({
    id: 1,
    maintenance: false,
    training: false,
    dryrun: false,
    from_name: "",
  });
  const [isLoading, setIsLoading] = useState(true);

  const getConfig = async () => {
    try {
      const tokenInfo = await fetchSession();
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/config`;
      const headers = {
        headers: {
          Authorization: `Bearer ${tokenInfo.accessToken}`,
        },
      };
      const response = await axios.get<{ config: ConfigSettings }>(
        apiUrl,
        headers
      );
      const data = response.data.config;
      setSettings({
        id: data.id || 1,
        maintenance: data.maintenance ?? false,
        training: data.training ?? false,
        dryrun: data.dryrun ?? false,
        from_name: data.from_name || "",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("API呼び出しエラー", error);
      setIsLoading(false);
    }
  };

  const updateConfig = async (newSettings: ConfigSettings) => {
    try {
      const tokenInfo = await fetchSession();
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/config?id=1`;
      const headers = {
        headers: {
          Authorization: `Bearer ${tokenInfo.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const requestData = {
        id: newSettings.id,
        maintenance: newSettings.maintenance,
        training: newSettings.training,
        dryrun: newSettings.dryrun,
        from_name: newSettings.from_name || "",
      };
      const response = await axios.put(apiUrl, requestData, headers);
      setSettings(newSettings);
      return response.data;
    } catch (error) {
      console.error("エラーが発生しました:", error);
      throw error;
    }
  };

  useEffect(() => {
    getConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfigContext.Provider
      value={{
        settings,
        updateConfig,
        refreshConfig: getConfig,
        isLoading,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextValue => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};
